import { noop } from 'lodash';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ViewOptionsMappedType,
  mapViewFromApi,
} from '~/clientApi/whalarModeLists';
import TabTimeElapsed from '~/components/TabTimeElapsed';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetSharedSavedList } from '~/hooks/api/useSavedLists';
import { useSharedLinkAccessEmail } from '~/hooks/api/useSavedLists/useSharedLinkAccessEmail';
import {
  useGetShareLink,
  useSetShareLinkAccess,
  useUpdateShareLinkAccess,
} from '~/hooks/api/useShareLinks';
import { EmailAccessScreen } from '~/screens/EmailAccessScreen';

import { useActiveView } from '../WhalarModeScreen/hooks/useActiveView';
import { SharedListContent } from './components/SharedListContent';

export function WhalarModeSharedListScreen() {
  const [shareLinkAccessId, setShareLinkAccessId] = useState(null);
  const [isEmailAccessOpen, setIsEmailAccessOpen] = useState(false);

  const router = useRouter();
  const { id: token } = router.query as { id: string };

  const { data: link, status } = useGetShareLink({ sharedToken: token });
  const createSharelinkAccess = useSetShareLinkAccess();
  const updateSharelinkAccess = useUpdateShareLinkAccess();
  const { accessEmail, updateAccessEmail } = useSharedLinkAccessEmail();

  const isSharedLinkAvailable = status === 'success' && link;
  const sharedToken = link?.token && `share-link-${link?.token}`;
  const view = link?.options?.view as ViewOptionsMappedType;

  const { data: list } = useGetSharedSavedList({ sharedToken });

  // TODO: this hook should be from another file
  const { activeView, setActiveView } = useActiveView();

  useEffect(() => {
    trackEvent('wm-shared-link-view', {
      'wm-shared-category': 'sourcing-list',
    });
  }, []);

  const updateShareLinkAccess = useCallback(
    (payload: { email?: string; time?: number }) => {
      if (!(shareLinkAccessId && link)) return;

      updateSharelinkAccess.mutate({
        shareLinkId: link.id,
        shareLinkAccessId,
        ...payload,
      });
    },
    [shareLinkAccessId, link, updateSharelinkAccess],
  );

  const handleSubmit = useCallback(
    (email: string) => {
      if (!list) return;

      updateAccessEmail(email);
      setIsEmailAccessOpen(false);
      updateShareLinkAccess({ email });
    },
    [updateAccessEmail, setIsEmailAccessOpen, updateShareLinkAccess, list],
  );

  useEffect(() => {
    if (list === undefined || link === undefined) return;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link?.entityType, list?.category, list?.name, list?.agencyName]);

  useEffect(() => {
    view && setActiveView(mapViewFromApi(view));
  }, [view, setActiveView]);

  useEffect(() => {
    link && setIsEmailAccessOpen(link.isEmailRequired && !accessEmail);
  }, [link, list, accessEmail]);

  // This effect is used to create a shareLinkAccess
  useEffect(() => {
    if (!isSharedLinkAvailable) return;

    createSharelinkAccess.mutate(
      {
        shareLinkId: link.id,
        email: accessEmail,
      },
      {
        onSuccess: ({ data: linkAccess }) => {
          setShareLinkAccessId(linkAccess.id);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharedLinkAvailable]);

  const handleOnLeaveTab = useCallback(
    (time: number) => updateShareLinkAccess({ time }),
    [updateShareLinkAccess],
  );

  const pageContent = useMemo(
    () =>
      list && (
        <SharedListContent
          activeView={activeView}
          setActiveView={setActiveView}
          list={list}
          sharedToken={sharedToken ?? token}
          onNoteOpen={noop}
          link={link}
        />
      ),
    [activeView, list, link, sharedToken, token, setActiveView],
  );

  const emailAccessModal = useMemo(
    () =>
      list && (
        <EmailAccessScreen
          agencyName={list.agencyName || ''}
          agencyLogo={list.agencyLogo}
          isOpen={isEmailAccessOpen}
          listName={list.name}
          managerName={list.manager?.name || list.agencyName || 'Whalar Group'}
          onSubmit={handleSubmit}
        />
      ),
    [list, isEmailAccessOpen, handleSubmit],
  );

  return (
    <>
      {shareLinkAccessId && <TabTimeElapsed onLeaveTab={handleOnLeaveTab} />}
      {pageContent}
      {emailAccessModal}
    </>
  );
}
